/**
 * @fileoverview Theme manager for theme rotation and progression.
 * Theme definitions are now in separate theme classes.
 */

import { AnimalsTheme } from '../themes/concrete/emoji/animals/AnimalsTheme.js';
import { BaseTheme } from '../themes/abstract/BaseTheme.js';
import { FruitsTheme } from '../themes/concrete/emoji/FruitsTheme.js';
import { LettersTheme } from '../themes/concrete/LettersTheme.js';
import { NumbersTheme } from '../themes/concrete/NumbersTheme.js';
import { MixTheme } from '../themes/concrete/emoji/MixTheme.js';
import { VehiclesTheme } from '../themes/concrete/emoji/VehiclesTheme.js';
import { ShapesTheme } from '../themes/concrete/ShapesTheme.js';
import type { PositionStrategy } from '../themes/strategies/PositionStrategy.js';
import type { IColorStrategy } from '../themes/strategies/IColorStrategy.js';
import { CircularPositionStrategy } from '../themes/strategies/CircularPositionStrategy.js';
import { HorizontalPositionStrategy } from '../themes/strategies/HorizontalPositionStrategy.js';
import { VerticalPositionStrategy } from '../themes/strategies/VerticalPositionStrategy.js';
import { BodyPartsTheme } from '../themes/concrete/emoji/BodyPartsTheme.js';
import { ClothingTheme } from '../themes/concrete/emoji/ClothingTheme.js';
import { ColorsTheme } from '../themes/concrete/emoji/ColorsTheme.js';
import { FoodTheme } from '../themes/concrete/emoji/FoodTheme.js';
import { HouseholdTheme } from '../themes/concrete/emoji/HouseholdTheme.js';
import { MusicTheme } from '../themes/concrete/emoji/MusicTheme.js';
import { NatureTheme } from '../themes/concrete/emoji/NatureTheme.js';
import { SpaceTheme } from '../themes/concrete/emoji/SpaceTheme.js';
import { SportsTheme } from '../themes/concrete/emoji/SportsTheme.js';
import { WeatherTheme } from '../themes/concrete/emoji/WeatherTheme.js';
// import { SmallPositionTestStrategy } from '../themes/strategies/SmallPositionTestStrategy.js';
// import { allLabels } from '../themes/Labels.js';
import { BirdsTheme } from '../themes/concrete/emoji/animals/BirtsTheme.js';
import { InsectsTheme } from '../themes/concrete/emoji/animals/InsectsTheme.js';
import { MammalsTheme } from '../themes/concrete/emoji/animals/MammalsTheme.js';
import { ReptilesTheme } from '../themes/concrete/emoji/animals/ReptilesTheme.js';
import { ActivitiesTheme } from '../themes/concrete/emoji/ActivitiesTheme.js';
import { BooksTheme } from '../themes/concrete/emoji/BooksTheme.js';
import { BuildingsTheme } from '../themes/concrete/emoji/BuildingsTheme.js';
import { FlagsTheme } from '../themes/concrete/emoji/FlagsTheme.js';
import { OceanTheme } from '../themes/concrete/emoji/OceanTheme.js';
import { SchoolTheme } from '../themes/concrete/emoji/SchoolTheme.js';
import { SeaTheme } from '../themes/concrete/emoji/SeaTheme.js';
import { SymbolsTheme } from '../themes/concrete/emoji/SymbolsTheme.js';
import { TechTheme } from '../themes/concrete/emoji/TechTheme.js';
import { PremiumService } from './PremiumService.js';

export interface ThemeOptions {
    hasMovement?: boolean;
    numPairs?: number;
    isPremium?: boolean;
    shapeTypes?: string[];
    fillTarget?: boolean;
    positionStrategy?: PositionStrategy;
    colorStrategy?: IColorStrategy;
    isSequential?: boolean;
    labels?: (number | string)[] | null;
}

/**
 * Theme service for game variety.
 * Handles theme rotation logic and persistence.
 */
export class ThemeService {
    private static readonly STORAGE_KEY = 'toddlerGame_themeProgress';
    private static readonly COMPLETIONS_PER_THEME = 1;

    private static trackThemeAnalytics(
        themeIndex: number,
        theme: BaseTheme,
        cycleCount: number,
        isPremiumUser: boolean,
        source: string
    ): void {
        try {
            const win = window as any;
            if (typeof win.gtag !== 'function') return;

            const themeName = (theme as any)?.constructor?.name || `Theme-${themeIndex}`;

            win.gtag('event', 'theme_state', {
                theme_index: themeIndex,
                theme_name: themeName,
                cycle_count: cycleCount || 0,
                is_premium_user: isPremiumUser ? 1 : 0,
                source: source
            });

            win.gtag('set', 'user_properties', {
                current_theme_index: String(themeIndex),
                current_theme_name: themeName,
                cycle_count: String(cycleCount || 0),
                premium_user: isPremiumUser ? 'true' : 'false'
            });
        } catch (e) {
            // Analytics must never break gameplay
        }
    }

    static getNumberPremiumThemes(): number {
        return ThemeService.getThemeSequence(true).filter(theme => theme.isPremium).length; 
    }

    /**
     * Get the sequence of themes in rotation order.
     */
    static getThemeSequence(isPremiumUser: boolean = false): BaseTheme[] {
        // Theme rotation sequence - using class instances
        let options: ThemeOptions = { isPremium: false };
        let circular = new CircularPositionStrategy();
        let horizontal = new HorizontalPositionStrategy();
        let vertical = new VerticalPositionStrategy();
        // let small = new SmallPositionTestStrategy();

        // Create combined animals array from all animal subcategories
        // const combinedAnimals = [
        //     ...allLabels.birds, 
        //     ...allLabels.mammals, 
        //     ...allLabels.reptiles, 
        //     ...allLabels.insects, 
        //     ...allLabels.sea
        // ];
        
        let allThemes: BaseTheme[] = [


            new ShapesTheme({...options, positionStrategy: vertical, numPairs: 2, isPremium: false }),
            new AnimalsTheme({...options, positionStrategy: vertical, numPairs: 3, isPremium: false }),
            new ShapesTheme({...options, positionStrategy: vertical, numPairs: 3, isSequential: false, isPremium: false }),
            new SpaceTheme({...options, positionStrategy: vertical, numPairs: 3, isSequential: true, isPremium: false}),

            new AnimalsTheme({...options, positionStrategy: horizontal, numPairs: 4, isSequential: true}),
            new BirdsTheme({...options, positionStrategy: vertical, numPairs: 4, isSequential: true}),
            new InsectsTheme({...options, positionStrategy: circular, numPairs: 5, isSequential: true}),
            new ActivitiesTheme({...options, positionStrategy: circular, numPairs: 3, isSequential: true}),
            new MammalsTheme({...options, positionStrategy: horizontal, numPairs: 4, isSequential: false}),
            new ReptilesTheme({...options, positionStrategy: vertical, numPairs: 5, isSequential: false}),

            new NumbersTheme({...options, positionStrategy: horizontal, numPairs: 3, isSequential: true}),

            new BuildingsTheme({...options, positionStrategy: horizontal, numPairs: 3, isSequential: false}),
            new FlagsTheme({...options, positionStrategy: circular, numPairs: 4, isSequential: true}),
            new OceanTheme({...options, positionStrategy: horizontal, numPairs: 6, isSequential: false}),
            new SeaTheme({...options, positionStrategy: circular, numPairs: 5, isSequential: true}),
            new SymbolsTheme({...options, positionStrategy: circular, numPairs: 3, isSequential: true}),

            new BooksTheme({...options, positionStrategy: vertical, numPairs: 4, isSequential: false}),

            new NumbersTheme({...options, positionStrategy: horizontal, numPairs: 3, isSequential: true}),

            new TechTheme({...options, positionStrategy: horizontal, numPairs: 4, isSequential: false}),
            // new TimeTheme({...options, positionStrategy: horizontal, numPairs: 6, isSequential: false}),
            // new ToolsTheme({...options, positionStrategy: horizontal, numPairs: 6, isSequential: true}),

            new LettersTheme({...options, positionStrategy: circular, numPairs: 2, isSequential: true}),

            new SchoolTheme({...options, positionStrategy: vertical, numPairs: 6, isSequential: true}),


            new BodyPartsTheme({...options, positionStrategy: horizontal, numPairs: 5}),
            new ClothingTheme({...options, positionStrategy: vertical, numPairs: 5, isSequential: true}),

            new LettersTheme({...options, positionStrategy: circular, numPairs: 2, isSequential: true}),

            new ColorsTheme({...options, positionStrategy: circular, numPairs: 6, isSequential: true}),


            new FoodTheme({...options, positionStrategy: vertical, numPairs: 5, isSequential: true}),
            new FruitsTheme({...options, positionStrategy: circular, numPairs: 6, isSequential: true}),

            new ShapesTheme({...options, positionStrategy: horizontal, numPairs: 4}),

            new LettersTheme({...options, positionStrategy: vertical, numPairs: 2, isSequential: true}),
            
            new NumbersTheme({...options, positionStrategy: horizontal, numPairs: 3, isSequential: true}),

            new HouseholdTheme({...options, positionStrategy: circular, numPairs: 5, isSequential: true}),
            new MusicTheme({...options, positionStrategy: vertical, numPairs: 5, isSequential: true}),

            new NumbersTheme({...options, positionStrategy: horizontal, numPairs: 3, isSequential: true}),

            new NatureTheme({...options, positionStrategy: circular, numPairs: 6, isSequential: true}),
            new MixTheme({...options, positionStrategy: horizontal, numPairs: 4}),

            new LettersTheme({...options, positionStrategy: vertical, numPairs: 2, isSequential: true}),

            new SpaceTheme({...options, positionStrategy: vertical, numPairs: 5}),


            new SportsTheme({...options, positionStrategy: circular, numPairs: 6, isSequential: true}),

            new LettersTheme({...options, positionStrategy: vertical, numPairs: 2, isSequential: true}),

            new VehiclesTheme({...options, positionStrategy: horizontal, numPairs: 5}),
            new WeatherTheme({...options, positionStrategy: vertical, numPairs: 5, isSequential: true}),

            new LettersTheme({...options, positionStrategy: vertical, numPairs: 2, isSequential: true}),

        ];
        
        
        // Filter themes based on premium status
        if (!isPremiumUser) {
            const allThemesNonPremium = allThemes.filter(theme => !theme.isPremium);
            return allThemesNonPremium;
        }
        
        return allThemes;
    }

    /**
     * Load theme progress from localStorage.
     * @returns {{currentThemeIndex: number, completionsInCurrentTheme: number, cycleCount: number}}
     */
    static loadProgress(): { currentThemeIndex: number; completionsInCurrentTheme: number; cycleCount: number } {
        try {
            const saved = localStorage.getItem(ThemeService.STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    currentThemeIndex: parsed.currentThemeIndex || 0,
                    completionsInCurrentTheme: parsed.completionsInCurrentTheme || 0,
                    cycleCount: parsed.cycleCount || 0
                };
            }
        } catch (e) {
            console.log('Failed to load theme progress:', e);
        }
        return { currentThemeIndex: 0, completionsInCurrentTheme: 0, cycleCount: 0 };
    }
    
    /**
     * Save theme progress to localStorage.
     * @param {number} themeIndex - Current theme index
     * @param {number} completions - Completions in current theme
     * @param {number} cycleCount - Number of complete cycles through all themes
     */
    static saveProgress(themeIndex: number, completions: number, cycleCount: number = 0): void {
        try {
            const progress = {
                currentThemeIndex: themeIndex,
                completionsInCurrentTheme: completions,
                cycleCount: cycleCount
            };
            localStorage.setItem(ThemeService.STORAGE_KEY, JSON.stringify(progress));
        } catch (e) {
            console.log('Failed to save theme progress:', e);
        }
    }
    
    /**
     * Get current active theme.
     */
    static getCurrentThemeFromStorage(isPremiumUser: boolean): BaseTheme {
        const progress = ThemeService.loadProgress();
        const sequence = ThemeService.getThemeSequence(isPremiumUser);
        if (sequence.length === 0) {
            throw new Error('No available themes for the current user status.');
        }
        
        // Ensure index is within bounds
        const safeIndex = Math.min(progress.currentThemeIndex, sequence.length - 1);
        const currentTheme = sequence[safeIndex];
        ThemeService.trackThemeAnalytics(safeIndex, currentTheme, progress.cycleCount || 0, isPremiumUser, 'load');
        return currentTheme;
    }
    
    /**
     * Record a theme completion and check if we should rotate.
     */
    static recordCompletion(isPremiumUser: boolean = false): { shouldRotate: boolean; nextTheme: BaseTheme } {
        const progress = ThemeService.loadProgress();
        const sequence = ThemeService.getThemeSequence(isPremiumUser);
        
        if (sequence.length === 0) {
            throw new Error('No available themes for the current user status.');
        }
        progress.completionsInCurrentTheme++;
        
        // Check if we've completed this theme enough times
        if (progress.completionsInCurrentTheme >= ThemeService.COMPLETIONS_PER_THEME) {
            // Move to next theme
            const nextIndex = (progress.currentThemeIndex + 1) % sequence.length;
            
            // Increment cycle count when wrapping back to theme 0
            if (nextIndex === 0) {
                let oldCycleCount = progress.cycleCount || 0;
                progress.cycleCount = (progress.cycleCount || 0) + 1;
                console.log(`🎉 Completed cycle #${progress.cycleCount}! Starting over...`);

                const countPremiumThemes = ThemeService.getNumberPremiumThemes();
                
                // Show upgrade prompt on first cycle completion for non-premium users
                if (!isPremiumUser && progress.cycleCount !== oldCycleCount && countPremiumThemes > 0 ) {
                    const userChoice = confirm(
                        '🎉 Congratulations! You\'ve completed all free themes!\n\n' +
                        'Want to unlock 40+ more premium themes and activities?\n\n' +
                        'Click "OK" to purchase premium for $2.99\n' +
                        'Click "Cancel" to start over from the beginning'
                    );
                    
                    if (userChoice) {
                        // User chose "Buy" - redirect to Stripe checkout
                        PremiumService.initStripeCheckout();
                        
                        // Don't rotate yet, keep on current theme until payment completes
                        ThemeService.trackThemeAnalytics(
                            progress.currentThemeIndex,
                            sequence[progress.currentThemeIndex],
                            progress.cycleCount || 0,
                            isPremiumUser,
                            'purchase_prompt'
                        );
                        return {
                            shouldRotate: false,
                            nextTheme: sequence[progress.currentThemeIndex]
                        };
                    }
                    // User chose "Cancel/Start from beginning" - continue with normal flow
                    else {
                        progress.currentThemeIndex = 0;
                        progress.completionsInCurrentTheme = 0;
                        ThemeService.saveProgress(progress.currentThemeIndex, progress.completionsInCurrentTheme, progress.cycleCount);
                        ThemeService.trackThemeAnalytics(
                            progress.currentThemeIndex,
                            sequence[progress.currentThemeIndex],
                            progress.cycleCount || 0,
                            isPremiumUser,
                            'cycle_restart'
                        );
                        return {
                            shouldRotate: true,
                            nextTheme: sequence[progress.currentThemeIndex]
                        };
                    }
                }
            }
            
            progress.currentThemeIndex = nextIndex;
            progress.completionsInCurrentTheme = 0;

            
            ThemeService.saveProgress(progress.currentThemeIndex, progress.completionsInCurrentTheme, progress.cycleCount);
            ThemeService.trackThemeAnalytics(
                progress.currentThemeIndex,
                sequence[progress.currentThemeIndex],
                progress.cycleCount || 0,
                isPremiumUser,
                'rotate'
            );
            
            return {
                shouldRotate: true,
                nextTheme: sequence[progress.currentThemeIndex]
            };
        } else {
            // Stay on current theme
            ThemeService.saveProgress(progress.currentThemeIndex, progress.completionsInCurrentTheme, progress.cycleCount || 0);
            ThemeService.trackThemeAnalytics(
                progress.currentThemeIndex,
                sequence[progress.currentThemeIndex],
                progress.cycleCount || 0,
                isPremiumUser,
                'stay'
            );
            
            return {
                shouldRotate: false,
                nextTheme: sequence[progress.currentThemeIndex]
            };
        }
    }
    
    /**
     * Reset theme progress (for testing).
     */
    static resetProgress(): void {
        ThemeService.saveProgress(0, 0, 0);
    }
    
    /**
     * Get the current cycle count (number of times all themes have been completed).
     * @returns {number} The cycle count
     */
    static getCycleCount(): number {
        const progress = ThemeService.loadProgress();
        return progress.cycleCount || 0;
    }
}